<template>
  <vue-final-modal
    :value="visible"
    name="ad-consent-modal"
    :classes="['ad-consent-modal__container']"
    :styles="{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }"
    :content-class="['ad-consent-modal__content', 'v--modal-box', 'v--modal']"
    :click-to-close="true"
    :esc-to-close="true"
    :lock-scroll="true"
    attach="body"
    @input="onVisibleInput"
  >
    <div class="v--modal-close-top">
      <el-button
        class="adconsent_close"
        @click="closeModal"
        icon="el-icon-circle-close"
        style="font-size: 1.4rem; color: #909399"
        type="text"
      ></el-button>
    </div>
    <div class="adconsent_popup">
      <h3 class="adconsent_h3">プライバシーポリシー改定のお知らせ</h3>

      <div class="adconsent_body" ref="scrollBody" @scroll="onBodyScroll">
        <p class="adconsent_txt">
          このたびプライバシーポリシーを改定しました。
        </p>
        <p class="adconsent_txt">
          同意いただくことで、あなたの転職・就職活動に関連する求人広告が優先的に表示されるようになります。
        </p>
        <p class="adconsent_txt">
          情報は暗号化処理（ハッシュ化）を経て提供され、氏名・住所などの個人を直接特定する情報は含まれません。
        </p>
        <p class="adconsent_txt">
          マイページからいつでも停止できます。詳しくは<a
            href="https://www.yagish.jp/privacy_policy/"
            target="_blank"
            rel="noopener noreferrer"
            >プライバシーポリシー</a
          >をご確認ください。
        </p>
      </div>

      <p v-if="!scrolledToBottom" class="adconsent_note">
        ※同意いただける場合は、最下部まで<br />スクロールして同意を押下してください。
      </p>
      <button
        class="adconsent_btn"
        :class="{ 'is-active': scrolledToBottom }"
        :disabled="!scrolledToBottom"
        @click="onAgreeAll"
      >
        同意する
      </button>
      <a href="#" class="adconsent_decline" @click.prevent="closeModal"
        >同意しない</a
      >
    </div>
  </vue-final-modal>
</template>

<script setup lang="ts">
/**
 * 広告同意モーダル（vue-final-modal）。
 * ログイン時は mitt の auth_state_changed、非ログインは useAuth().whenAuthReady()。
 * 手動表示は mitt の show-ad-consent-modal。vue-js-modal / event bus は使わない。
 */
import axios from 'axios'
import {
  getCurrentInstance,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  ref,
  watch
} from 'vue'
import { AD_NETWORKS } from '@/config/ad-networks'
import { useAuth } from '@/composables/useAuth'
import { AUTH_STATE_CHANGED, emitter } from '@/plugins/emitter'
import {
  getConsentFlag,
  setConsentFlag,
  getPromptableNetworks,
  getPromptCount,
  incrementPromptCount,
  AD_PROMPT_MAX,
  fetchAllFlagsFromServer,
  saveConsentFlagToServer,
  fireNetwork
} from '@/utils/ad-consent'

type AdNetwork = (typeof AD_NETWORKS)[number]
type ConsentFlags = Record<string, string>
type VarunaClient = {
  endPoints: {
    getAdConsentFlags: string
    updateAdConsentFlag: string
  }
}

const SHOW_AD_CONSENT_MODAL = 'show-ad-consent-modal'

const vm = getCurrentInstance()!.proxy as unknown as { $varuna: VarunaClient }
const varuna = vm.$varuna
const { user, isAuthenticated, getClient, whenAuthReady } = useAuth()

const visible = ref(false)
const undecidedNetworks = ref<AdNetwork[]>([])
const modalShown = ref(false)
const decisionMade = ref(false)
const scrolledToBottom = ref(false)
const scrollBody = ref<HTMLElement | null>(null)
let authCheckInFlight = false

function currentUid(): string | undefined {
  return getClient()?.user?.uid ?? user.value?.uid
}

function currentIsAuthenticated(): boolean {
  return !!(getClient()?.user?.uid || isAuthenticated.value)
}

function onVisibleInput(value: boolean) {
  visible.value = value
}

function closeModal() {
  visible.value = false
}

function showAll() {
  undecidedNetworks.value = AD_NETWORKS.slice()
  visible.value = true
}

function onModalOpened() {
  incrementPromptCount()
  decisionMade.value = false
  scrolledToBottom.value = false
  nextTick(() => {
    const el = scrollBody.value
    if (!el) return
    if (el.scrollHeight <= el.clientHeight + 2) scrolledToBottom.value = true
  })
}

function onBodyScroll() {
  const el = scrollBody.value
  if (!el) return
  scrolledToBottom.value = el.scrollTop + el.clientHeight >= el.scrollHeight - 8
}

async function persistFlags(agreed: boolean) {
  const uid = currentUid()
  const authenticated = currentIsAuthenticated()
  for (const network of undecidedNetworks.value) {
    if (authenticated && uid) {
      await saveConsentFlagToServer(axios, varuna, uid, network.id, agreed)
    } else {
      setConsentFlag(network.id, agreed ? '1' : '0')
    }
    if (agreed) fireNetwork(network.id)
  }
}

async function onAgreeAll() {
  decisionMade.value = true
  await persistFlags(true)
  visible.value = false
}

async function onModalClosed() {
  if (decisionMade.value) return
  decisionMade.value = true
  await persistFlags(false)
}

async function onAuthStateChanged() {
  if (modalShown.value || authCheckInFlight) return
  const uid = currentUid()
  if (!uid) return
  authCheckInFlight = true
  try {
    const flags = (await fetchAllFlagsFromServer(
      axios,
      varuna,
      uid
    )) as ConsentFlags
    for (const network of AD_NETWORKS) {
      const dbVal = flags[network.id]
      if (dbVal === '1' || dbVal === '0') continue
      const cookieVal = getConsentFlag(network.id)
      if (cookieVal === '1' || cookieVal === '0') {
        await saveConsentFlagToServer(
          axios,
          varuna,
          uid,
          network.id,
          cookieVal === '1'
        )
        flags[network.id] = cookieVal
      }
    }
    if (modalShown.value) return
    if (getPromptCount() >= AD_PROMPT_MAX) return
    undecidedNetworks.value = AD_NETWORKS.filter((n) => flags[n.id] !== '1')
    if (undecidedNetworks.value.length > 0) {
      modalShown.value = true
      visible.value = true
    }
  } finally {
    authCheckInFlight = false
  }
}

function maybeShowForGuest() {
  if (modalShown.value || currentIsAuthenticated()) return
  if (getPromptCount() >= AD_PROMPT_MAX) return
  undecidedNetworks.value = getPromptableNetworks()
  if (undecidedNetworks.value.length > 0) {
    modalShown.value = true
    visible.value = true
  }
}

watch(visible, (open) => {
  if (open) {
    onModalOpened()
    return
  }
  onModalClosed()
})

onBeforeMount(() => {
  emitter.on(AUTH_STATE_CHANGED, onAuthStateChanged)
  emitter.on(SHOW_AD_CONSENT_MODAL, showAll)
  whenAuthReady().then((authUser) => {
    if (authUser) {
      onAuthStateChanged()
      return
    }
    maybeShowForGuest()
  })
})

onBeforeUnmount(() => {
  emitter.off(AUTH_STATE_CHANGED, onAuthStateChanged)
  emitter.off(SHOW_AD_CONSENT_MODAL, showAll)
})
</script>

<style scoped>
.v--modal-close-top {
  display: flex;
  justify-content: flex-end;
  height: 15px;
}

/* 他の要素を動かさず、×ボタンだけを下方向にずらす（数値で調整可） */
.adconsent_close {
  transform: translateY(18px);
  padding: 0; /* el-button の内側余白を除去し上部を詰める */
}

.adconsent_popup {
  padding: 10px;
}

.adconsent_h3 {
  font-weight: bold;
  font-size: 1.05rem;
  line-height: 1.5;
  color: #004d40;
  margin-bottom: 8px;
}

.adconsent_body {
  max-height: 220px;
  overflow-y: scroll;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 14px 16px;
  font-size: 0.875rem;
  line-height: 1.7;
  line-break: strict;
  text-wrap: pretty;
  color: #333;
  scrollbar-width: thin;
  scrollbar-color: #bbb #f0f0f0;
  margin-bottom: 16px;
}

.adconsent_body::-webkit-scrollbar {
  width: 6px;
}

.adconsent_body::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 3px;
}

.adconsent_body::-webkit-scrollbar-thumb {
  background: #bbb;
  border-radius: 3px;
}

.adconsent_subh {
  font-size: 0.9rem;
  font-weight: 700;
  color: #004d40;
  margin: 14px 0 8px;
}

.adconsent_list {
  padding-left: 1.25em;
  margin-bottom: 12px;
}

.adconsent_list li {
  margin-bottom: 8px;
}

.adconsent_txt {
  margin-bottom: 10px;
}

.adconsent_txt a {
  color: #4a90d9;
  text-decoration: underline;
}

.adconsent_btn {
  display: block; /* inline-block 由来の下部余白（後続空白の行ボックス）を除去 */
  width: 100%;
  padding: 13px;
  background: #ccc;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 700;
  cursor: not-allowed;
  transition: background 0.2s;
}

.adconsent_btn.is-active {
  background: #0f9934;
  cursor: pointer;
}

.adconsent_btn.is-active:hover {
  background: #0a7a28;
}

.adconsent_note {
  text-align: center;
  font-size: 0.75rem;
  line-height: 1.6;
  color: #888;
  margin-top: 6px;
}

/* 「同意しない」テキストリンク（同意ボタンの下・控えめな見た目） */
.adconsent_decline {
  display: block;
  text-align: center;
  margin-top: 12px;
  font-size: 0.85rem;
  color: #888;
  text-decoration: underline;
  cursor: pointer;
}

.adconsent_decline:hover {
  color: #555;
}
</style>

<!-- content-class は vue-final-modal 内部ノードのため scoped 外 -->
<style>
.ad-consent-modal__container {
  z-index: 1001;
}

.ad-consent-modal__content.v--modal-box.v--modal {
  position: relative;
  box-sizing: border-box;
  width: 460px;
  max-width: calc(100% - 32px);
  height: auto;
  margin: 20px auto;
  padding: 8px 12px 16px;
  border-radius: 8px;
  background: #fff;
}
</style>
